import { useEffect, useState } from "react";
import { ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

function findPath(node, targetId, path = []) {
  // Match by `id` or `slug` to support new API shape
  if (node.id === targetId || node.slug === targetId) return [...path, node];

  for (const child of node.children || []) {
    const result = findPath(child, targetId, [...path, node]);
    if (result) return result;
  }

  return null;
}

function trimTitleAfterDash(title) {
  if (typeof title !== "string") return title;
  let depth = 0;
  for (let i = 0; i < title.length - 2; i += 1) {
    const char = title[i];
    if (char === "(") depth += 1;
    if (char === ")") depth = Math.max(0, depth - 1);

    if (
      depth === 0 &&
      title[i] === " " &&
      title[i + 1] === "-" &&
      title[i + 2] === " "
    ) {
      return title.slice(0, i).trim();
    }
  }

  return title;
}

export default function Breadcrumbs({ currentNode, tree }) {
  // 1. All hooks must be called unconditionally, at the top
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    setIsExpanded(false);
  }, [currentNode?.id]);

  // 2. Early returns come AFTER hooks
  if (!tree) return null;

  // 3. Derived values
  const crumbs = currentNode
    ? findPath(tree, currentNode.id) || [tree]
    : [tree];

  const MAX_VISIBLE_CRUMBS = 4;
  const TAIL_CRUMB_COUNT = 2;

  const shouldCollapse = crumbs.length > MAX_VISIBLE_CRUMBS;
  const visibleCrumbs = shouldCollapse
    ? isExpanded
      ? [...crumbs, { control: "collapse" }]
      : [crumbs[0], { control: "ellipsis" }, ...crumbs.slice(-TAIL_CRUMB_COUNT)]
    : crumbs;

  return (
    <div className="flex flex-wrap items-center gap-2 text-sm text-gray-400 mb-4">
      {visibleCrumbs.map((c, i) => {
        if (c.control === "ellipsis") {
          return (
            <span key="crumb-ellipsis" className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setIsExpanded(true)}
                className="text-gray-500 hover:text-(--color-text-primary) text-sm"
                aria-label="Expand breadcrumbs"
              >
                ...
              </button>
              {i < visibleCrumbs.length - 1 && (
                <ChevronRight className="h-3 w-3 text-gray-500" />
              )}
            </span>
          );
        }

        if (c.control === "collapse") {
          return (
            <span key="crumb-collapse" className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setIsExpanded(false)}
                className="text-gray-500 hover:text-(--color-text-primary) text-sm"
                aria-label="Collapse breadcrumbs"
              >
                Collapse
              </button>
            </span>
          );
        }

        return (
          <span key={c.id} className="flex items-center gap-2">
            <Link
              to={
                c.type === "lab"
                  ? `/lab/${c.slug || c.id}`
                  : `/boxes/${c.slug || c.id}`
              }
              className="accent-link text-sm"
            >
              {trimTitleAfterDash(c.title)}
            </Link>
            {i < visibleCrumbs.length - 1 && (
              <ChevronRight className="h-3 w-3 text-gray-500" />
            )}
          </span>
        );
      })}
    </div>
  );
}